import { Ban, Check } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import type {
  CreateFailureField,
  CreateProjectMode,
  CreateProjectRequest,
  ProjectSummary,
} from "@/api/hooks";
import {
  CreateTimedOutError,
  UNRESOLVED_CREATE_MESSAGE,
  classifyCreateFailure,
  queryKeys,
  useCreateProjectMutation,
  usePreviewTargetsQuery,
  useProjectsQuery,
} from "@/api/hooks";
import { ENVIRONMENT_LABEL } from "@/lib/labels";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { FieldError, joinDescriptionIds } from "@/ui/FieldError";
import { Field, Input, Select } from "@/ui/Input";
import { DeniedState, UnavailableState } from "@/ui/states";

const MODE = {
  from_seed: {
    label: "Base seed",
    description: "A clean project built from the versioned base seed.",
  },
  clone_project: {
    label: "Copy of an existing project",
    description:
      "An independent draft baseline copied from a project you can already open.",
  },
} satisfies Record<CreateProjectMode, { label: string; description: string }>;

const MODES = Object.keys(MODE) as CreateProjectMode[];

/**
 * What a clone carries and what it never carries.
 *
 * Both lists come from the `CloneProject` contract description, split in two:
 * "Copies versioned configuration/adoption references only — never patients,
 * sessions, PHI, credentials, audit actors, or live deployment state." No
 * exclusion here is invented, and they are shown to the clinician rather than
 * assumed.
 *
 * #203: the last one is worded target-neutrally instead of quoted. "Live
 * deployment state" is correct where the contract says it — the release
 * workflow is what maps a published artifact to a tenant and environment — but
 * naming it in a Project dialog presents deployment state as a property a
 * Project has, and the amendment says it has none. The thing excluded is
 * unchanged: a copy still carries no publication or release state of the
 * project it was copied from. The same dialog already stops repeating this
 * description's "The clone inherits the source project's tenant and
 * environment" on the same grounds.
 */
export const CLONE_COPIES = [
  "Versioned configuration references",
  "Adoption references",
];

export const CLONE_NEVER_COPIES = [
  "Patients",
  "Sessions",
  "PHI",
  "Credentials",
  "Audit actors",
  "Publication and release state",
];

const NAME_MAX_LENGTH = 200;
const SEED_VERSION_MAX_LENGTH = 128;

/**
 * How long Cancel waits for the projects refetch it promises before closing
 * anyway (claude review, PR #670 round 4).
 *
 * The refetch is awaited so the list has been refreshed at least once after
 * an unresolved create before the clinician can act on it — but a refetch is
 * not guaranteed to SETTLE: with the connection gone (or the tab hidden)
 * TanStack's retryer PAUSES the attempt until the browser is back online
 * (`queryClient.ts`, #327), and an awaited pause is a dialog whose every
 * control is disabled "while refreshing" with no way out. Cancel must always
 * be a way out, so the wait is bounded; a paused refetch still completes on
 * its own once the connection returns.
 */
export const DISMISS_REFRESH_TIMEOUT_MS = 5_000;

type FieldErrors = {
  name?: string;
  seedVersion?: string;
  sourceProjectId?: string;
  previewTenantId?: string;
};

type FieldKey = keyof FieldErrors;

/**
 * The client-side enablement matrix (#619, audit row PRJ-NEW-03), per mode:
 *
 * | field           | from_seed                 | clone_project |
 * |-----------------|---------------------------|---------------|
 * | name            | required, trimmed, ≤200   | same          |
 * | previewTenantId | required, from the list   | same          |
 * | seedVersion     | optional, ≤128            | not sent      |
 * | sourceProjectId | not sent                  | required      |
 *
 * ONE function feeds both consumers — the Create button's enablement and the
 * submit handler's field errors — so the two can never disagree about what a
 * valid form is. Only deterministic, client-knowable rules live here; whether
 * a name is taken, a seed exists, or a target is still enabled is the
 * server's answer, attributed to its field when it arrives.
 *
 * Pure and exported so the matrix above is TESTED as a table, not inferred
 * from clicks.
 */
export function computeFieldBlocks(form: {
  mode: CreateProjectMode;
  name: string;
  seedVersion: string;
  sourceProjectId: string;
  selectedTarget: string;
}): FieldErrors {
  const blocks: FieldErrors = {};
  const trimmedName = form.name.trim();
  if (trimmedName.length === 0) {
    blocks.name = "Enter a name for this project.";
  } else if (trimmedName.length > NAME_MAX_LENGTH) {
    blocks.name = `Use ${NAME_MAX_LENGTH} characters or fewer.`;
  }
  if (
    form.mode === "from_seed" &&
    form.seedVersion.trim().length > SEED_VERSION_MAX_LENGTH
  ) {
    blocks.seedVersion = `Use ${SEED_VERSION_MAX_LENGTH} characters or fewer.`;
  }
  if (form.mode === "clone_project" && form.sourceProjectId === "") {
    blocks.sourceProjectId = "Choose the project to copy.";
  }
  if (form.selectedTarget === "") {
    blocks.previewTenantId = "Choose a preview target.";
  }
  return blocks;
}

/** "a", "a and b", "a, b, and c" — the footer hint reads as a sentence. */
export function listInProse(items: readonly string[]): string {
  if (items.length <= 1) {
    return items.join("");
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function CopyManifest() {
  return (
    <div className="rounded-md border border-border bg-muted/40 p-3">
      <p className="text-sm font-medium text-foreground">
        What a copy contains
      </p>
      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Copied
          </p>
          <ul className="mt-1 space-y-1">
            {CLONE_COPIES.map((item) => (
              <li
                key={item}
                className="flex items-start gap-1.5 text-xs text-foreground"
              >
                <Check
                  aria-hidden="true"
                  // `--success` is the pale badge background; the -foreground
                  // token is the one that survives on a light surface.
                  className="mt-0.5 h-3 w-3 shrink-0 text-success-foreground"
                />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Never copied
          </p>
          <ul className="mt-1 space-y-1">
            {CLONE_NEVER_COPIES.map((item) => (
              <li
                key={item}
                className="flex items-start gap-1.5 text-xs text-foreground"
              >
                <Ban
                  aria-hidden="true"
                  className="mt-0.5 h-3 w-3 shrink-0 text-destructive"
                />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/**
 * Creates a project from the base seed or as an independent copy of one the
 * caller can already open.
 *
 * Mounted only while open, so every opening starts from a clean form and a
 * clean mutation without any reset logic to get wrong.
 *
 * `createTimeoutMs` is how long a create waits before its outcome is declared
 * unknown. It defaults to the product value and exists as a prop only so a
 * test can stage a service that answers AFTER the browser stopped waiting —
 * the one condition that cannot be induced against a healthy service.
 */
export function NewProjectDialog({
  onClose,
  createTimeoutMs,
}: {
  onClose: () => void;
  createTimeoutMs?: number;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mutation = useCreateProjectMutation(
    createTimeoutMs === undefined ? {} : { timeoutMs: createTimeoutMs },
  );

  // The projects the caller is authorized to open are the only ones they may
  // copy from. The index has usually cached this already.
  const sources = useProjectsQuery("");
  // Availability is derived from provisioned credentials and can change without
  // a deployment, so it is fetched when the choice is offered rather than held
  // for the session (#202 PR C).
  const targets = usePreviewTargetsQuery();

  const [mode, setMode] = useState<CreateProjectMode>("from_seed");
  const [name, setName] = useState("");
  const [seedVersion, setSeedVersion] = useState("");
  const [sourceProjectId, setSourceProjectId] = useState("");
  const [previewTenantId, setPreviewTenantId] = useState("");
  /**
   * Which controls have been left (blurred) at least once, and whether a
   * submit has been refused client-side. A control's client-side block is
   * SHOWN only once one of those is true — otherwise every required field
   * would open the dialog already marked invalid — but it is never STORED:
   * the message is derived from the current input each render, so it cannot
   * outlive the keystroke that satisfied it (#619; claude review, PR #670).
   */
  const [touched, setTouched] = useState<Partial<Record<FieldKey, true>>>({});
  const [attempted, setAttempted] = useState(false);
  /**
   * The key that makes retrying THIS create safe (#619): the service replays
   * same key + same body as the original project, so a lost response cannot
   * double-create. Rotated by `noteBodyChanged` on every body-affecting edit,
   * which keeps "same key ⇔ same body" true from the client's side — a
   * changed form is a different logical create, never an `idempotency_conflict`.
   */
  const [idempotencyKey, setIdempotencyKey] = useState(() =>
    crypto.randomUUID(),
  );
  /**
   * The request that was actually sent, held verbatim while its outcome is
   * pending or unknown (claude review, PR #670). A retry of an unresolved
   * create resends THIS object — never a body re-derived from the form or
   * from a listing that may have refetched underneath it — so "same key ⇔
   * same body" is a fact about bytes, not about the form having stayed
   * still. Cleared the moment the outcome is known.
   */
  const [submitted, setSubmitted] = useState<CreateProjectRequest | null>(
    null,
  );
  /**
   * A definite refusal that arrived AFTER the wait ran out: the service kept
   * working on the request the browser stopped waiting for, and answered it
   * (see `CreateTimedOutError`). Rendered exactly as if it had arrived in
   * time; it unfreezes the form once no other uncertain attempt remains.
   */
  const [lateRefusal, setLateRefusal] = useState<{ error: unknown } | null>(
    null,
  );
  /**
   * Every attempt whose answer is still capable of changing the outcome of
   * this logical create. Retrying a timed-out request starts another attempt,
   * but a quick refusal from that retry does not prove the older handler did
   * not already pass validation and commit. The body and key therefore stay
   * frozen until all such attempts refuse, or any one of them confirms the
   * idempotent success.
   *
   * A transport failure has no outcome promise to settle later, so it remains
   * uncertain until a byte-identical retry returns the project or the
   * clinician explicitly abandons the wait with Cancel.
   */
  const pendingTimeoutsRef = useRef(new Set<CreateTimedOutError>());
  const hasUnobservableAttemptRef = useRef(false);
  const [hasUnresolvedAttempt, setHasUnresolvedAttempt] = useState(false);
  const acceptedProjectRef = useRef(false);
  const mountedRef = useRef(true);
  /** Cancel after an unresolved create is refreshing the list before closing. */
  const [dismissing, setDismissing] = useState(false);
  /**
   * Cancel was pressed on an unresolved create. From then on a late 201 must
   * NOT navigate — the clinician asked for the list, not the project — even
   * in the window before the parent unmounts this dialog; the list refresh
   * the late answer owes still happens in the mutation (claude review,
   * PR #670 round 4).
   */
  const abandonedRef = useRef(false);
  // The same synchronous latch `handleSubmit` has: two dismissals landing
  // before React commits `dismissing` must not both run the bounded wait
  // and both call `onClose` (claude review, PR #670 round 5).
  const dismissInFlightRef = useRef(false);
  /** The terminal (denied / unavailable) body, so a refusal that replaces the form can still take focus. */
  const terminalRef = useRef<HTMLDivElement | null>(null);
  // Mutation state is rendered asynchronously. This latch closes the gap
  // between an event handler starting work and React committing that state.
  const submitInFlightRef = useRef(false);
  /**
   * Focus-after-refusal (#619): incremented when a submit is refused —
   * client-side or by the server — so the effect below can move focus to the
   * first control marked `aria-invalid`, after React has committed those
   * marks. A counter rather than a boolean, so refusing the SAME field twice
   * still re-focuses it.
   */
  const [focusTick, setFocusTick] = useState(0);
  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const syncUnresolvedAttempts = useCallback(() => {
    const unresolvedAttempt =
      hasUnobservableAttemptRef.current || pendingTimeoutsRef.current.size > 0;
    setHasUnresolvedAttempt(unresolvedAttempt);
    return unresolvedAttempt;
  }, []);

  const acceptCreatedProject = useCallback(
    (created: ProjectSummary) => {
      if (acceptedProjectRef.current) {
        return;
      }
      acceptedProjectRef.current = true;
      pendingTimeoutsRef.current.clear();
      hasUnobservableAttemptRef.current = false;
      if (mountedRef.current) {
        setHasUnresolvedAttempt(false);
        setSubmitted(null);
      }
      if (mountedRef.current && !abandonedRef.current) {
        void navigate(`/projects/${encodeURIComponent(created.id)}`);
      }
    },
    [navigate],
  );

  // The submit control lives in the dialog footer, outside the form element,
  // so the two are joined by `form=`.
  const formId = useId();
  const modeGroupId = useId();
  const nameErrorId = useId();
  const seedVersionErrorId = useId();
  const sourceErrorId = useId();
  const previewTargetErrorId = useId();
  const formErrorId = useId();
  const cloneReasonId = useId();
  const createBlockedHintId = useId();

  const sourceItems: ProjectSummary[] = sources.data?.items ?? [];
  const cloneUnavailableReason = sources.isPending
    ? "Loading the projects you can copy from."
    : sources.isError
      ? "The projects you can copy from could not be loaded, so there is nothing to choose."
      : sourceItems.length === 0
        ? "No projects are available to copy."
        : null;

  const targetItems: string[] = targets.data?.items ?? [];
  // `isError` and `data` are NOT mutually exclusive: a failed REFETCH leaves the
  // last successful data in place and sets `status: "error"`, and `staleTime: 0`
  // means a refetch runs on every mount. So the error arm is gated on there
  // being nothing to fall back on — otherwise reopening the dialog during a
  // blip listed real, pickable targets underneath "could not be loaded" while
  // the footer disabled submit (claude review, round 4).
  // #567 keeps three failures three DIFFERENT sentences: an authorization
  // denial (the caller may not list targets), a provisioning/readiness failure
  // (the service could not derive them), and the successful EMPTY answer (no
  // cell is enrolled). Collapsing them is how "I turned my role off" reads as
  // "the platform is down".
  const targetUnavailableReason = targets.isPending
    ? "Loading the preview targets you can choose from."
    : targetItems.length === 0
      ? targets.isError
        ? targets.error instanceof ApiError && targets.error.isForbidden
          ? "Your account is not authorized to list preview targets."
          : targets.error instanceof ApiError && targets.error.status === 503
            ? "Preview target provisioning is unavailable: the service could not derive the enabled targets. Retry once provisioning recovers."
            : "The preview targets could not be loaded, so there is nothing to choose."
        : "No preview target is available yet. One must be provisioned before a project can be created."
      : null;

  // The refetch removes the revoked OPTION; this removes the revoked VALUE.
  // A selection that has left the list is DERIVED away rather than synced back
  // into state with an effect — `react-hooks/set-state-in-effect` forbids the
  // effect, and rightly: the stored id is not a second source of truth, it is
  // only meaningful against the current list.
  //
  // Without this the state kept the dead id, the select rendered blank (a
  // controlled value matching no option is `selectedIndex === -1`), the
  // required-field guard still passed because the STATE was non-empty, and the
  // next click re-sent the same id for the same 422 — the loop the refetch was
  // added to break (claude review, round 4).
  const selectedTarget = targetItems.includes(previewTenantId)
    ? previewTenantId
    : "";

  // The copy source, by the same rule and for the same reason: a source that
  // has left the listing — the 404 below refetches it — must not stay a
  // non-empty id that passes the required-field guard and is resent verbatim
  // from a select that renders blank (claude review, PR #670).
  const selectedSourceId = sourceItems.some(
    (item) => item.id === sourceProjectId,
  )
    ? sourceProjectId
    : "";
  const selectedSource = sourceItems.find(
    (item) => item.id === selectedSourceId,
  );

  /**
   * The enablement matrix, DERIVED synchronously in render from the current
   * inputs — never read back from state a submit attempt wrote — so the
   * Create button's enablement cannot lag the keystroke that changed it.
   * `handleSubmit` consumes the same value: one function, two consumers,
   * zero opportunity to disagree (#619, PRJ-NEW-03).
   */
  const fieldBlocks = computeFieldBlocks({
    mode,
    name,
    seedVersion,
    sourceProjectId: selectedSourceId,
    selectedTarget,
  });
  const canSubmit = Object.keys(fieldBlocks).length === 0;

  const failureSource =
    lateRefusal !== null
      ? lateRefusal.error
      : mutation.isError
        ? mutation.error
        : null;
  const classifiedFailure =
    failureSource === null ? null : classifyCreateFailure(failureSource, mode);
  const currentFailureIsUnknown =
    classifiedFailure?.kind === "retryable" &&
    classifiedFailure.outcome === "unknown";
  const unresolved = hasUnresolvedAttempt || currentFailureIsUnknown;
  // An unresolved older attempt is more authoritative than a refusal from a
  // newer retry: the older service handler can still commit. Keep rendering
  // the locked, byte-identical-retry state until that uncertainty is gone.
  const failure = unresolved
    ? ({
        kind: "retryable",
        outcome: "unknown",
        message: UNRESOLVED_CREATE_MESSAGE,
      } as const)
    : classifiedFailure;
  const terminal =
    failure && (failure.kind === "denied" || failure.kind === "unavailable")
      ? failure
      : null;
  /**
   * The request that was sent has not been answered in a way that says
   * whether the row exists. Everything that could change the body — or
   * rotate the key — is inert until it is (claude review, PR #670): the
   * outstanding request holds the old body and key, and an edit here would
   * either navigate a late success to a row the form no longer describes, or
   * turn the retry into a DIFFERENT create with a fresh key beside a first
   * one that may have committed.
   */
  const frozen = mutation.isPending || unresolved || dismissing;

  const fieldFailure = failure?.kind === "field" ? failure : null;
  const submitErrorFor = (field: CreateFailureField) =>
    fieldFailure?.field === field ? fieldFailure.message : null;
  // The service's sentence first — it knows more than the matrix does — then
  // the client-side block, shown once the control has been left or a submit
  // refused. Both derive from the current render; nothing here is stored.
  const shownBlock = (field: FieldKey) =>
    attempted || touched[field] ? (fieldBlocks[field] ?? null) : null;
  const nameError = submitErrorFor("name") ?? shownBlock("name");
  const seedVersionError =
    submitErrorFor("seedVersion") ?? shownBlock("seedVersion");
  const sourceError =
    submitErrorFor("sourceProjectId") ?? shownBlock("sourceProjectId");
  // `retryable` renders in the same form-level slot as `validation`: both
  // keep the form mounted with every entered value, and both are answered by
  // pressing Create again — and, unless the outcome is unknown, by editing
  // first (#619).
  const formSubmitError =
    failure && (failure.kind === "validation" || failure.kind === "retryable")
      ? failure.message
      : null;
  // The service named `previewTenantId`; the form honours that rather than
  // showing a message about "these project details" beside an unmarked control.
  const previewTargetError =
    (failure && failure.kind === "previewTarget" ? failure.message : null) ??
    shownBlock("previewTenantId");

  function markTouched(field: FieldKey) {
    setTouched((current) =>
      current[field] ? current : { ...current, [field]: true },
    );
  }

  /**
   * Every body-affecting edit funnels here (#619): the changed form is a
   * DIFFERENT logical create, so the idempotency key rotates with it, keeping
   * "same key ⇔ same body" true client-side.
   *
   * Whether the rendered REFUSAL also goes depends on what it named. A
   * refusal attributed to one control — the duplicate name, the unknown seed,
   * the vanished source, the revoked target — is about that control's value,
   * and editing a different control leaves that value exactly as refused;
   * clearing it then would drop the mark from a name that is still the
   * duplicate the service just refused (claude review, PR #670 round 3). A
   * form-level refusal, or a mode switch (`field` undefined — the body swaps
   * wholesale), is answered by any edit.
   *
   * Unreachable while `frozen` — every control that calls it is inert then —
   * and guarded anyway, because the invariant it protects is the one an
   * outstanding request depends on.
   */
  function noteBodyChanged(field?: FieldKey) {
    if (frozen) {
      return;
    }
    setIdempotencyKey(crypto.randomUUID());
    setSubmitted(null);
    const namedField: FieldKey | null =
      failure?.kind === "field"
        ? failure.field
        : failure?.kind === "previewTarget"
          ? "previewTenantId"
          : null;
    if (namedField !== null && field !== undefined && namedField !== field) {
      return;
    }
    setLateRefusal(null);
    if (mutation.isError) {
      mutation.reset();
    }
  }

  /**
   * What makes an `aria-disabled` control genuinely inert while frozen.
   *
   * `aria-disabled` alone only ANNOUNCES; a native select still opens and
   * moves on the arrow keys, and a radio still checks on click — React
   * would snap the controlled value back after the ignored `onChange`, but
   * a control that visibly moves and springs back is not the "not operable"
   * ARIA defines, and it is exactly the state in which the form must prove
   * its body untouched. So activation is blocked at the event, the way
   * `Button` blocks its click, while the control keeps its focus stop (Tab
   * still moves) so the alert that explains the freeze is reachable from it.
   * The repo's other target select (`PreviewTargetControl`) sets
   * `aria-disabled` only when there is nothing to choose, which needs no
   * such guard; this dialog freezes controls that DO have options.
   */
  const inertWhileFrozen = frozen
    ? {
        "aria-disabled": true as const,
        onMouseDown: (event: MouseEvent<HTMLElement>) => event.preventDefault(),
        onClick: (event: MouseEvent<HTMLElement>) => event.preventDefault(),
        onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
          if (event.key !== "Tab") {
            event.preventDefault();
          }
        },
      }
    : {};

  /**
   * After a refused submit, focus lands on the first control marked
   * `aria-invalid` (document order — the order the fields render in), or on
   * the form-level alert when no control is marked. Runs after commit, so
   * the marks it queries are the ones this refusal just rendered.
   */
  useEffect(() => {
    if (focusTick === 0) {
      return;
    }
    const root = formRef.current;
    if (!root) {
      // A denial or an unbuilt endpoint replaced the form wholesale; the
      // control that had focus is gone, so the state that replaced it takes
      // it (claude review, PR #670 round 4).
      terminalRef.current?.focus();
      return;
    }
    const invalid = root.querySelector<HTMLElement>('[aria-invalid="true"]');
    const alert = root.querySelector<HTMLElement>("[data-form-alert]");
    (invalid ?? alert)?.focus();
  }, [focusTick]);

  /**
   * Everything a refusal owes the form BEYOND rendering its message — and
   * the one place it is owed from, so a refusal that arrives in time and one
   * that arrives after the wait cannot diverge (claude review, PR #670 round
   * 3: the late path used to render the message and nothing else, so a late
   * `preview_target_not_enabled` left the dead target in a never-refetched
   * list, selectable and resubmittable — the loop the refetch exists to
   * break — with no focus move and the frozen request still held).
   *
   * `useCallback` so the late-outcome effect below can list it as a
   * dependency without resubscribing on every render: its inputs are the
   * mode (which cannot change while a request is outstanding) and stable
   * setters.
   */
  const noteRefusal = useCallback(
    (error: unknown) => {
      // Another same-key attempt may finish after a confirmed success. The
      // success is authoritative for the logical create; a later refusal from
      // a concurrent retry must not restore an error state while navigation is
      // in progress.
      if (acceptedProjectRef.current) {
        return;
      }
      const failed = classifyCreateFailure(error, mode);
      if (failed.kind === "retryable" && failed.outcome === "unknown") {
        if (error instanceof CreateTimedOutError) {
          pendingTimeoutsRef.current.add(error);
        } else {
          // A dropped connection/bare gateway failure has no promise carrying
          // the eventual server answer. Only an idempotent success or Cancel
          // can resolve that uncertainty from this client.
          hasUnobservableAttemptRef.current = true;
        }
      }
      const unknownAttemptRemains = syncUnresolvedAttempts();
      // The listing is now known to be stale — it offered a choice the
      // service refuses. Refetching is the only thing that makes the retry
      // the clinician is being asked for a different request; the derived
      // selections drop the dead value with the dead option.
      if (failed.kind === "previewTarget") {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.previewTargets,
        });
      }
      // The bare root, as `useCreateProjectMutation`'s own `onSuccess` uses
      // it: every search variant of the listing must go, and the per-project
      // caches underneath the same root belong to OTHER projects that a
      // create does not change — marking them stale costs a refetch on their
      // next mount, not now.
      if (failed.kind === "field" && failed.field === "sourceProjectId") {
        void queryClient.invalidateQueries({ queryKey: ["projects"] });
      }
      // Same key + different body means the key no longer describes THIS
      // form; mint a fresh one so the retry is judged on its own body.
      if (
        !unknownAttemptRemains &&
        error instanceof ApiError &&
        error.code === "idempotency_conflict"
      ) {
        setIdempotencyKey(crypto.randomUUID());
      }
      // A known answer releases the frozen body only after every older
      // uncertain attempt has also answered. A retry refusal cannot erase an
      // older handler that may still commit the same logical create.
      if (!unknownAttemptRemains) {
        setSubmitted(null);
      }
      // The refusal is about to render its marks; put focus on the first.
      setFocusTick((tick) => tick + 1);
    },
    [mode, queryClient, syncUnresolvedAttempts],
  );

  /**
   * The wait ran out, but the request is still running (`CreateTimedOutError`
   * carries it). Whatever the service eventually answers resolves the unknown
   * outcome: a 201 is the committed row, so the dialog goes there exactly as
   * it would have in time; a refusal is rendered as if it had arrived in
   * time — same message, same refetch, same focus move (`noteRefusal`) —
   * and the form unfreezes only when no other uncertain attempt remains. A
   * retry does not supersede this listener: it can be refused after this older
   * handler passed validation but before it commits. Any confirmed success
   * therefore wins; a refusal resolves only its own attempt. The list refresh
   * a late 201 owes the index happens in the mutation, so it survives the
   * dialog being dismissed.
   */
  useEffect(() => {
    const error = mutation.error;
    if (
      !(error instanceof CreateTimedOutError) ||
      acceptedProjectRef.current
    ) {
      return;
    }
    pendingTimeoutsRef.current.add(error);
    syncUnresolvedAttempts();
    error.outcome.then(
      (created) => {
        acceptCreatedProject(created);
      },
      (lateError: unknown) => {
        pendingTimeoutsRef.current.delete(error);
        if (!mountedRef.current || acceptedProjectRef.current) {
          return;
        }
        const lateFailure = classifyCreateFailure(lateError, mode);
        const lateOutcomeUnknown =
          lateFailure.kind === "retryable" && lateFailure.outcome === "unknown";
        if (lateOutcomeUnknown) {
          noteRefusal(lateError);
        } else if (!syncUnresolvedAttempts()) {
          setLateRefusal({ error: lateError });
          noteRefusal(lateError);
        }
      },
    );
  }, [
    acceptCreatedProject,
    mode,
    mutation.error,
    noteRefusal,
    syncUnresolvedAttempts,
  ]);

  /**
   * Every way out of the dialog after a settled outcome funnels here (#619).
   *
   * An UNRESOLVED create is the one that can hide a committed row: the wait
   * ran out or the connection dropped while the service may still be writing
   * — Starlette does not cancel a handler on client disconnect — so the
   * project may well exist by the time the clinician presses Cancel. This
   * cannot promise "Cancel leaves no Project": the API has no non-creating
   * outcome lookup (replaying the key CREATES when the first attempt did not
   * commit), so Cancel is an explicit abandonment of an unresolved create.
   * What it does promise is that the list underneath — mounted with the
   * client-wide 30s `staleTime` and `refetchOnWindowFocus: false`, so nothing
   * else would refresh it — has been refetched at least once AFTER the
   * failure before the clinician can act on it again, hence AWAITED rather
   * than fire-and-forget (claude review, PR #670) — but never for longer
   * than `DISMISS_REFRESH_TIMEOUT_MS`, because the refetch can pause rather
   * than fail. A commit that lands later still is caught by the timed-out
   * request's own late answer (see `useCreateProjectMutation`), which
   * invalidates the list after the commit — and, once Cancel was pressed,
   * only that: it no longer navigates.
   */
  async function dismiss() {
    // Cross-gated with `handleSubmit`: React has not necessarily committed
    // `isPending` when Cancel, Escape, or the overlay lands in the same tick as
    // submit. The synchronous submit latch is the only reliable evidence in
    // that window that dismissing would orphan an active create.
    if (
      submitInFlightRef.current ||
      mutation.isPending ||
      dismissing ||
      dismissInFlightRef.current
    ) {
      return;
    }
    dismissInFlightRef.current = true;
    if (unresolved) {
      abandonedRef.current = true;
      setDismissing(true);
      let bound: ReturnType<typeof setTimeout> | undefined;
      try {
        // A dropped connection has no late outcome promise. If its create did
        // commit, it also changed adoption pins, so mark every library count
        // projection stale while the Projects refresh remains the bounded,
        // awaited check that gates dismissal.
        void queryClient.invalidateQueries({ queryKey: ["library"] });
        await Promise.race([
          queryClient.invalidateQueries({ queryKey: ["projects"] }),
          new Promise<void>((resolve) => {
            bound = setTimeout(resolve, DISMISS_REFRESH_TIMEOUT_MS);
          }),
        ]);
      } finally {
        clearTimeout(bound);
        onClose();
      }
      return;
    }
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    // Single-flight. The submit control is already disabled while pending; this
    // also covers an Enter key that beats the re-render.
    // Cross-gated with `dismiss`: a submit event can land after Cancel but
    // before React commits `dismissing`. Once dismissal owns the dialog, no
    // request may leave it behind.
    if (
      submitInFlightRef.current ||
      dismissInFlightRef.current ||
      mutation.isPending ||
      dismissing
    ) {
      return;
    }

    let request: CreateProjectRequest;
    if (unresolved && submitted !== null) {
      // Byte-identical: the object that was sent, not a body rebuilt from a
      // form that is frozen precisely so it cannot have changed.
      request = submitted;
    } else {
      // The SAME matrix the Create button derives its enablement from — kept
      // here as well because a submit event can still reach the form while
      // the button reads as disabled, and a refusal must mark its fields and
      // move focus rather than silently do nothing (#619).
      if (!canSubmit) {
        setAttempted(true);
        setFocusTick((tick) => tick + 1);
        return;
      }
      const trimmedName = name.trim();
      request =
        mode === "from_seed"
          ? {
              mode: "from_seed",
              name: trimmedName,
              previewTenantId: selectedTarget,
              idempotencyKey,
              ...(seedVersion.trim()
                ? { seedVersion: seedVersion.trim() }
                : {}),
            }
          : {
              mode: "clone_project",
              name: trimmedName,
              sourceProjectId: selectedSourceId,
              previewTenantId: selectedTarget,
              idempotencyKey,
            };
    }

    setLateRefusal(null);
    setSubmitted(request);
    submitInFlightRef.current = true;
    mutation.mutate(request, {
      onError: noteRefusal,
      onSuccess(created) {
        // The route is built from the server-issued identifier and from nothing
        // else — never from the name the clinician typed.
        acceptCreatedProject(created);
      },
      onSettled() {
        submitInFlightRef.current = false;
      },
    });
  }

  function renderBody() {
    if (terminal) {
      // `role="alert"` and a focus stop, as `ErrorState` carries and the
      // shared `DeniedState`/`UnavailableState` frames do not: this state
      // REPLACES the form the clinician just submitted, so without them a
      // screen reader hears nothing and keyboard focus falls to <body>
      // (claude review, PR #670 round 4).
      return (
        <div
          ref={terminalRef}
          role="alert"
          tabIndex={-1}
          className="outline-none"
        >
          {terminal.kind === "denied" ? (
            <DeniedState
              title="You cannot create projects here"
              description="Ask a workspace administrator for permission to create projects in this environment."
            />
          ) : (
            <UnavailableState
              title="Project creation is not available yet"
              description="The Editor Service accepts this request but has not implemented it yet, so no project was created. This resolves when the endpoint is deployed — there is nothing to retry."
              blockedBy="Blocked by issue #27 — project registry and safe clone-from-seed contract."
            />
          )}
        </div>
      );
    }

    return (
      <form
        id={formId}
        ref={formRef}
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <fieldset className="space-y-2" aria-describedby={modeGroupId}>
          <legend className="text-sm font-medium text-foreground">
            Start from
          </legend>
          <p id={modeGroupId} className="sr-only">
            Choose whether this project starts from the versioned base seed or
            from a copy of an existing project.
          </p>
          {MODES.map((candidate) => {
            const blocked =
              candidate === "clone_project" && cloneUnavailableReason !== null;
            return (
              <div key={candidate} className="flex items-start gap-2">
                <input
                  type="radio"
                  id={`${modeGroupId}-${candidate}`}
                  name={`${modeGroupId}-mode`}
                  value={candidate}
                  checked={mode === candidate}
                  // Native `disabled` for a BLOCKED clone rather than this
                  // application's usual `aria-disabled`, because the reason
                  // is rendered as visible text below rather than hidden in a
                  // tooltip — there is nothing left for a focus stop to
                  // reveal. FROZEN is different: the reason is the form-level
                  // alert, the radio keeps its focus stop, and activation is
                  // blocked at the event (`inertWhileFrozen`).
                  disabled={blocked}
                  {...inertWhileFrozen}
                  onChange={() => {
                    if (frozen) {
                      return;
                    }
                    setMode(candidate);
                    // A mode switch swaps which fields the body carries, so
                    // every recorded refusal is about a different request now.
                    setTouched({});
                    setAttempted(false);
                    noteBodyChanged();
                  }}
                  className="mt-1"
                  {...(blocked
                    ? { "aria-describedby": cloneReasonId }
                    : {})}
                />
                <label
                  htmlFor={`${modeGroupId}-${candidate}`}
                  className="text-sm"
                >
                  <span className="font-medium text-foreground">
                    {MODE[candidate].label}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {MODE[candidate].description}
                  </span>
                  {blocked ? (
                    <span
                      id={cloneReasonId}
                      className="block text-xs text-muted-foreground"
                    >
                      {cloneUnavailableReason}
                    </span>
                  ) : null}
                </label>
              </div>
            );
          })}
        </fieldset>

        <Field label="Project name">
          {({ inputId }) => (
            <>
              <Input
                id={inputId}
                value={name}
                maxLength={NAME_MAX_LENGTH}
                // `readOnly`, not `disabled`, while frozen: the value stays
                // selectable, focusable and announced, and the reason it
                // refuses input is the form-level alert beside it.
                readOnly={frozen}
                onChange={(event) => {
                  if (frozen) {
                    return;
                  }
                  setName(event.target.value);
                  // A name the service rejected is no longer the name being
                  // typed, so the rejection stops applying to it.
                  noteBodyChanged("name");
                }}
                onBlur={() => markTouched("name")}
                // #203: a clinical example, not an organisation. "Sheba
                // Rehabilitation" taught the reader that a Project is named
                // after the customer it deploys for; Projects are shared,
                // reusable clinical workspaces, and the first example a
                // clinician sees is where that is either learned or unlearned.
                placeholder="e.g. Post-stroke rehabilitation"
                aria-invalid={nameError ? true : undefined}
                {...(nameError ? { "aria-describedby": nameErrorId } : {})}
              />
              {nameError ? (
                <FieldError id={nameErrorId} message={nameError} />
              ) : null}
            </>
          )}
        </Field>

        {/*
          Required in BOTH modes, and outside the branch for that reason: a
          clone does not inherit its source's target (#202 PR C), so the copy
          path has exactly as much to choose as the seed path.

          No preselection, deliberately — not even when one target is available.
          "Explicitly selected" is the acceptance criterion, and a default that
          happened to be right in a single-target deployment would become a
          silent wrong answer the day a second target is provisioned.
        */}
        <Field
          label="Preview target"
          // Through `description`, not a loose <p>: `Field` only populates
          // `describedBy` when it is given one, so a visible-only paragraph is
          // never announced. That matters most in the state it explains — the
          // one where there is nothing to choose — which is why the select
          // below marks itself `aria-disabled` rather than `disabled` and stays
          // in the tab order, so the reason has a focus stop to be read from
          // (claude review, rounds 1 and 3).
          description={
            targetUnavailableReason ??
            "Which Tenant Liv runtime and test patients this project's previews run against. It can be changed later."
          }
        >
          {({ inputId, describedBy }) => (
            <>
              <Select
                id={inputId}
                value={selectedTarget}
                // `aria-disabled`, not the native attribute, on `Button`'s
                // documented reasoning: native `disabled` removes the control
                // from the tab order, and this is precisely the state whose
                // REASON a screen-reader user needs to reach. With nothing to
                // choose the select carries only the placeholder, so it cannot
                // produce a value anyway, and the submit is gated separately.
                aria-disabled={targetUnavailableReason !== null || undefined}
                {...inertWhileFrozen}
                onChange={(event) => {
                  if (frozen) {
                    return;
                  }
                  setPreviewTenantId(event.target.value);
                  // The target the service refused is no longer the one
                  // selected, so its rejection stops applying — the same
                  // reasoning as the name field above, and without this the
                  // "no longer available" message and `aria-invalid` survive
                  // onto the target the clinician just picked FROM the
                  // refreshed list (claude review, round 6).
                  noteBodyChanged("previewTenantId");
                }}
                onBlur={() => markTouched("previewTenantId")}
                aria-invalid={previewTargetError ? true : undefined}
                aria-describedby={joinDescriptionIds(
                  describedBy,
                  previewTargetError ? previewTargetErrorId : undefined,
                )}
              >
                <option value="">Choose a preview target</option>
                {targetItems.map((target) => (
                  <option key={target} value={target}>
                    {target}
                  </option>
                ))}
              </Select>
              {previewTargetError ? (
                <FieldError id={previewTargetErrorId} message={previewTargetError} />
              ) : null}
            </>
          )}
        </Field>

        {formSubmitError ? (
          <p
            id={formErrorId}
            role="alert"
            // Focusable so the focus-after-refusal effect can land here when
            // no single control is marked — `data-form-alert` distinguishes
            // this slot from the per-field `FieldError`s, which also carry
            // `role="alert"` but whose CONTROL is the right focus target.
            data-form-alert
            tabIndex={-1}
            className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          >
            {formSubmitError}
          </p>
        ) : null}

        {mode === "from_seed" ? (
          <>
            <Field
              label="Seed version (optional)"
              description="Leave empty to use the latest base seed."
            >
              {({ inputId, describedBy }) => (
                <>
                  <Input
                    id={inputId}
                    value={seedVersion}
                    maxLength={SEED_VERSION_MAX_LENGTH}
                    readOnly={frozen}
                    onChange={(event) => {
                      if (frozen) {
                        return;
                      }
                      setSeedVersion(event.target.value);
                      noteBodyChanged("seedVersion");
                    }}
                    onBlur={() => markTouched("seedVersion")}
                    placeholder="Latest"
                    aria-invalid={seedVersionError ? true : undefined}
                    aria-describedby={joinDescriptionIds(
                      describedBy,
                      seedVersionError ? seedVersionErrorId : undefined,
                    )}
                  />
                  {seedVersionError ? (
                    <FieldError
                      id={seedVersionErrorId}
                      message={seedVersionError}
                    />
                  ) : null}
                </>
              )}
            </Field>
          </>
        ) : (
          <>
            <Field label="Copy from">
              {({ inputId }) => (
                <>
                  <Select
                    id={inputId}
                    value={selectedSourceId}
                    {...inertWhileFrozen}
                    onChange={(event) => {
                      if (frozen) {
                        return;
                      }
                      setSourceProjectId(event.target.value);
                      noteBodyChanged("sourceProjectId");
                    }}
                    onBlur={() => markTouched("sourceProjectId")}
                    aria-invalid={sourceError ? true : undefined}
                    {...(sourceError
                      ? { "aria-describedby": sourceErrorId }
                      : {})}
                  >
                    <option value="">Choose a project</option>
                    {sourceItems.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Select>
                  {sourceError ? (
                    <FieldError id={sourceErrorId} message={sourceError} />
                  ) : null}
                </>
              )}
            </Field>

            {/*
              #203 DELETED this panel, and #202 PR C brought it back. Recorded
              because the reversal looks like a regression and is not.

              The removal was conditional on its own face: it said the panel
              could go because "an explicit preview-target selection that a
              clone must never inherit silently" would replace it, and that the
              registry and `previewTenantId` behind such a control "do not exist
              yet (#202)" — so the panel was removed rather than stubbed. #202
              PR C then built exactly that control, it now sits above this, and
              the condition is spent.

              What #203 actually objected to is gone and stays gone: the claim
              that a copy inherits the source project's TENANT. `ProjectSummary
              .environment` was never retired — #236 took `.tenant`, not this —
              so the panel states a real field, read-only, and points at the
              selector above rather than implying inheritance.

              Read-only context, not an input: cross-environment cloning is not
              offered, so there is no control here to disable — only a fact to
              state. It used to say a clone inherits the source's TENANT too,
              which has no referent since #202 PR D2a — there is no tenant axis
              on a Project at all (rows are keyed by environment and project;
              the only tenant-shaped field is the preview target) — and reads
              as a flat contradiction now that a required preview-target select
              sits above it (claude review, round 2).
            */}
            <div className="space-y-1.5">
              <span className="text-sm font-medium text-foreground">
                Environment
              </span>
              <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
                {selectedSource
                  ? (ENVIRONMENT_LABEL[selectedSource.environment] ??
                    selectedSource.environment)
                  : "Set by the project you copy from."}
              </p>
              <p className="text-xs text-muted-foreground">
                A copy lands in the source project&apos;s environment. Copying
                into a different environment is not offered, and the preview
                target is chosen above rather than inherited.
              </p>
            </div>
            <CopyManifest />
          </>
        )}
      </form>
    );
  }

  function renderFooter() {
    if (terminal) {
      return (
        <Button variant="outline" onClick={() => void dismiss()}>
          Close
        </Button>
      );
    }

    /*
      Create is DISABLED — `Button`'s disabled, which is `aria-disabled` plus a
      blocked activation, the way every disabled control in this application
      is disabled — whenever the client-side enablement matrix is unsatisfied,
      a create is in flight, or no target can be chosen at all (#619,
      PRJ-NEW-03). It used to announce `aria-disabled` while still activating,
      so what a screen reader called "dimmed, unavailable" a click operated —
      the ARIA definition of disabled is "not operable", and `Button`'s own
      contract says the same (claude review, PR #670).

      A dead control that silently ignores its required fields would be
      PRJ-NEW-03 inverted, so the button is never silent: the WHY sits beside
      it, wired through `aria-describedby`, in field-label terms rather than
      the field errors' own sentences — those belong to the marked controls,
      which show them the moment a control is left incomplete (`onBlur`).

      An UNRESOLVED create is the one state where the button is enabled
      although the matrix is not consulted: it resends the frozen request,
      and a listing that refetched underneath must not be able to gate that.
    */
    const hardDisabled =
      mutation.isPending ||
      dismissing ||
      (!unresolved && (targetUnavailableReason !== null || !canSubmit));
    const missing = unresolved
      ? []
      : [
          fieldBlocks.name ? "a project name" : null,
          // Only while a target CAN be chosen. With the select inert — listing
          // loading, failed, or empty — its own description already says why,
          // and "provide a preview target" would be an instruction the
          // clinician cannot follow: the round-1 objection to the old submit
          // path, which hard-disabling Create answered, must not come back as
          // a hint (claude review, PR #670).
          fieldBlocks.previewTenantId && targetUnavailableReason === null
            ? "a preview target"
            : null,
          fieldBlocks.sourceProjectId ? "a project to copy" : null,
          fieldBlocks.seedVersion ? "a shorter seed version" : null,
        ].filter((item): item is string => item !== null);
    const createBlockedReason =
      !mutation.isPending && missing.length > 0
        ? `To create, provide ${listInProse(missing)}.`
        : null;

    return (
      <>
        {createBlockedReason ? (
          <p
            id={createBlockedHintId}
            className="mr-auto self-center text-xs text-muted-foreground"
          >
            {createBlockedReason}
          </p>
        ) : null}
        {mutation.isPending ? null : (
          <Button
            variant="outline"
            disabled={dismissing}
            aria-busy={dismissing || undefined}
            onClick={() => void dismiss()}
          >
            {dismissing ? "Refreshing the projects list…" : "Cancel"}
          </Button>
        )}
        <Button
          type="submit"
          form={formId}
          disabled={hardDisabled}
          aria-describedby={createBlockedReason ? createBlockedHintId : undefined}
        >
          {mutation.isPending
            ? "Creating…"
            : unresolved
              ? "Try again"
              : "Create project"}
        </Button>
      </>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // Escape and the overlay must not orphan a create that is still in
        // flight, nor interrupt the refresh Cancel promised. A SETTLED outcome
        // is dismissable — `dismiss` owns what leaving means for each kind.
        if (!next && !mutation.isPending && !dismissing) {
          void dismiss();
        }
      }}
    >
      <DialogContent
        title="New project"
        description="Create a project from the versioned base seed, or as an independent copy of one you can already open."
        busy={mutation.isPending || dismissing}
        footer={renderFooter()}
      >
        {renderBody()}
      </DialogContent>
    </Dialog>
  );
}
